import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../../Config';

const GestionRetours = () => {
  const { livraisonId } = useParams();
  const navigate = useNavigate();
  const [livraison, setLivraison] = useState(null);
  const [evenement, setEvenement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [quantitesRecues, setQuantitesRecues] = useState({});
  const [etatsRetour, setEtatsRetour] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (livraisonId) {
      chargerDonnees();
    }
  }, [livraisonId]);

  const chargerDonnees = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(`${API_URL}/livraisons/${livraisonId}`, config);
      setLivraison(response.data);
      
      if (response.data.evenement) {
        setEvenement(response.data.evenement);
        
        const initialQuantites = {};
        const initialEtats = {};
        
        if (response.data.evenement.materiels) {
          response.data.evenement.materiels.forEach(materiel => {
            const key = materiel.id;
            initialQuantites[key] = materiel.pivot?.quantite_recue || 0;
            initialEtats[key] = materiel.pivot?.etat_retour || 'bon';
          });
        }
        
        setQuantitesRecues(initialQuantites);
        setEtatsRetour(initialEtats);
      } else {
        setMessage({
          type: 'warning',
          text: 'Les détails des matériels ne sont pas disponibles. Contactez l\'administrateur.'
        });
      }

    } catch (error) {
      console.error('Erreur chargement données:', error);
      
      let errorMessage = 'Erreur lors du chargement des données';
      if (error.response?.status === 403) {
        errorMessage = 'Accès refusé. Vous devez être connecté en tant que magasinier.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Livraison non trouvée.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuantiteChange = (materielId, value) => {
    if (!evenement?.materiels) return;
    
    const materiel = evenement.materiels.find(m => m.id === materielId);
    if (!materiel) return;
    
    const quantiteLivree = materiel.pivot?.quantite_livree || 0;
    const quantite = Math.max(0, Math.min(parseInt(value) || 0, quantiteLivree));
    
    setQuantitesRecues(prev => ({
      ...prev,
      [materielId]: quantite
    }));
  };

  const handleEtatChange = (materielId, etat) => {
    setEtatsRetour(prev => ({
      ...prev,
      [materielId]: etat
    }));
  };

  const handleSaveRetour = async (materielId) => {
    if (!evenement) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const quantiteRecue = quantitesRecues[materielId] || 0;
      const etatRetour = etatsRetour[materielId] || 'bon';
      
      await axios.patch(
        `${API_URL}/evenements/${evenement.id}/materiels/${materielId}/retour`,
        { 
          quantite_recue: quantiteRecue,
          etat_retour: etatRetour
        },
        config
      );

      setMessage({
        type: 'success',
        text: `Retour enregistré: ${quantiteRecue} unité(s), état: ${etatRetour}`
      });

      chargerDonnees();
    } catch (error) {
      console.error('Erreur enregistrement retour:', error);
      
      let errorMessage = 'Erreur lors de l\'enregistrement';
      if (error.response?.status === 403) {
        errorMessage = 'Permission refusée. Action réservée aux magasiniers.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Matériel non trouvé.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    if (!evenement || !evenement.materiels) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const promises = evenement.materiels.map(async (materiel) => {
        const quantiteRecue = quantitesRecues[materiel.id] || 0;
        const etatRetour = etatsRetour[materiel.id] || 'bon';
        const quantiteActuelle = materiel.pivot?.quantite_recue || 0;
        const etatActuel = materiel.pivot?.etat_retour || 'bon';
        
        if (quantiteRecue !== quantiteActuelle || etatRetour !== etatActuel) {
          return axios.patch(
            `${API_URL}/evenements/${evenement.id}/materiels/${materiel.id}/retour`,
            { 
              quantite_recue: quantiteRecue,
              etat_retour: etatRetour
            },
            config
          );
        }
        return Promise.resolve();
      });

      await Promise.all(promises);
      
      setMessage({
        type: 'success',
        text: 'Tous les retours ont été enregistrés'
      });

      chargerDonnees();
    } catch (error) {
      console.error('Erreur enregistrement global:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de l\'enregistrement global'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFinaliserRetour = async () => {
    if (!livraison) return;
    
    if (!window.confirm('Êtes-vous sûr de vouloir finaliser ce retour ? Cette action est définitive.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Format datetime pour MySQL (YYYY-MM-DD HH:MM:SS)
      const now = new Date();
      const dateRetour = now.toISOString().slice(0, 19).replace('T', ' ');
      
      // Utiliser la route PUT standard ou créer une route dédiée
      await axios.put(`${API_URL}/livraisons/${livraisonId}`, {
        date_retour: dateRetour,
        statut: 'retour_complet'
      }, config);

      setMessage({
        type: 'success',
        text: 'Retour finalisé avec succès'
      });

      // Recharger les données pour afficher la date mise à jour
      chargerDonnees();
      
      // Redirection après un délai
      setTimeout(() => {
        navigate('/magasinier/livraisons');
      }, 2000);

    } catch (error) {
      console.error('Erreur finalisation retour:', error);
      
      let errorMessage = 'Erreur lors de la finalisation';
      if (error.response?.data?.errors) {
        // Afficher les erreurs de validation Laravel
        const errors = Object.values(error.response.data.errors).flat();
        errorMessage = errors.join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMessage({
        type: 'error',
        text: errorMessage
      });
    }
  };

  const getEtatBadge = (etat) => {
    switch (etat) {
      case 'bon':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Bon état
          </span>
        );
      case 'endommage':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Endommagé
          </span>
        );
      case 'perdu':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Perdu
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Non retourné
          </span>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!livraison) {
    return (
      <div className="min-h-screen  p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-700">Livraison non trouvée</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/magasinier/livraisons')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover: transition-colors"
          >
            Retour aux livraisons
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestion des Retours</h1>
              <p className="text-gray-600 mt-1">
                Livraison #{livraison.id} - {evenement ? `Événement #${evenement.id} - ${evenement.beneficiaire?.nom}` : 'Chargement...'}
              </p>
            </div>
            
            <button
              onClick={() => navigate('/magasinier/livraisons')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover: transition-colors"
            >
              Retour aux livraisons
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Messages */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
            message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
            'bg-yellow-50 border border-yellow-200 text-yellow-700'
          }`}>
            <div className="flex items-center">
              <svg className={`w-5 h-5 mr-3 ${
                message.type === 'error' ? 'text-red-500' :
                message.type === 'success' ? 'text-green-500' : 'text-yellow-500'
              }`} fill="currentColor" viewBox="0 0 20 20">
                {message.type === 'error' ? (
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                ) : message.type === 'success' ? (
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                ) : (
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                )}
              </svg>
              <span>{message.text}</span>
            </div>
          </div>
        )}

        {/* Informations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations Retour</h3>
            <div className="space-y-2">
              <div>
                <div className="text-sm text-gray-500">Date Livraison</div>
                <div className="font-medium">{formatDate(livraison.date_livraison)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Date Retour</div>
                <div className={`font-medium ${livraison.date_retour ? 'text-green-600' : 'text-yellow-600'}`}>
                  {livraison.date_retour ? formatDate(livraison.date_retour) : 'À définir'}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Transport</div>
                <div className="font-medium">{livraison.transport_inclus ? 'Inclus' : 'Non inclus'}</div>
              </div>
            </div>
          </div>

          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Récapitulatif Retours</h3>
            <div className="space-y-3">
              {evenement && evenement.materiels ? (
                <>
                  <div className="flex justify-between">
                    <div className="text-sm text-gray-600">Matériels livrés</div>
                    <div className="font-medium text-blue-600">
                      {evenement.materiels.filter(m => m.pivot?.quantite_livree > 0).length || 0}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <div className="text-sm text-gray-600">Retournés</div>
                    <div className="font-medium text-green-600">
                      {evenement.materiels.filter(m => m.pivot?.quantite_recue > 0).length || 0}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <div className="text-sm text-gray-600">Non retournés</div>
                    <div className="font-medium text-yellow-600">
                      {evenement.materiels.filter(m => !m.pivot?.quantite_recue || m.pivot.quantite_recue === 0).length || 0}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <div className="text-sm text-gray-600">État</div>
                    <div className="font-medium">
                      {livraison.statut ? (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                          livraison.statut === 'retour_complet' ? 'bg-green-100 text-green-800' :
                          livraison.statut === 'retour_partiel' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {livraison.statut === 'retour_complet' ? 'Retour complet' :
                           livraison.statut === 'retour_partiel' ? 'Retour partiel' :
                           'Livrée'}
                        </span>
                      ) : 'N/A'}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-gray-500">Informations non disponibles</div>
              )}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {saving && (
          <div className="mb-6">
            <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 animate-pulse"></div>
            </div>
            <div className="text-center text-sm text-gray-600 mt-2">Enregistrement en cours...</div>
          </div>
        )}

        {/* Table des retours */}
        <div className="bg-white border rounded-lg shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Liste des matériels à retourner</h2>
            <p className="text-sm text-gray-500 mt-1">
              Gestion des quantités retournées et état
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Matériel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Livrée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Retournée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    État précédent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantité retournée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    État retour
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {evenement && evenement.materiels && evenement.materiels.length > 0 ? (
                  evenement.materiels
                    .filter(materiel => materiel.pivot?.quantite_livree > 0) // Afficher seulement les matériels livrés
                    .map((materiel) => {
                      const quantiteLivree = materiel.pivot?.quantite_livree || 0;
                      const quantiteRecue = materiel.pivot?.quantite_recue || 0;
                      const quantiteSaisie = quantitesRecues[materiel.id] || 0;
                      const etatSaisie = etatsRetour[materiel.id] || 'bon';
                      
                      return (
                        <tr key={materiel.id} className="hover: transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{materiel.nom}</div>
                              <div className="text-xs text-gray-500">{materiel.reference}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {quantiteLivree}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              quantiteRecue > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {quantiteRecue}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {getEtatBadge(materiel.pivot?.etat_retour || 'non_retourne')}
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <input
                                type="number"
                                value={quantiteSaisie}
                                onChange={(e) => handleQuantiteChange(materiel.id, e.target.value)}
                                min="0"
                                max={quantiteLivree}
                                disabled={saving}
                                className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              />
                              <div className="text-xs text-gray-500 mt-1">
                                Maximum: {quantiteLivree}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={etatSaisie}
                              onChange={(e) => handleEtatChange(materiel.id, e.target.value)}
                              disabled={saving}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                              <option value="bon">Bon état</option>
                              <option value="endommage">Endommagé</option>
                              <option value="perdu">Perdu</option>
                            </select>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleSaveRetour(materiel.id)}
                              disabled={saving || 
                                (quantiteSaisie === quantiteRecue && 
                                 etatSaisie === (materiel.pivot?.etat_retour || 'bon'))}
                              className={`p-2 rounded transition-colors ${
                                saving || (quantiteSaisie === quantiteRecue && etatSaisie === (materiel.pivot?.etat_retour || 'bon'))
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                              title="Enregistrer le retour"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        {evenement ? 'Aucun matériel à retourner' : 'Informations des matériels non disponibles'}
                      </div>
                      {!evenement && (
                        <button
                          onClick={chargerDonnees}
                          className="mt-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover: transition-colors text-sm"
                        >
                          Réessayer
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={chargerDonnees}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover: transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Actualiser
            </button>
            <button
              onClick={handleSaveAll}
              disabled={saving || !evenement || !evenement.materiels}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                saving || !evenement || !evenement.materiels
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer tous les retours'}
            </button>
          </div>
          
          <button
            onClick={handleFinaliserRetour}
            disabled={saving || livraison.date_retour}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              saving || livraison.date_retour
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {livraison.date_retour ? 'Retour déjà finalisé' : 'Finaliser le retour'}
          </button>
        </div>

        {/* Informations */}
        <div className="mt-8 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <div className="text-sm font-medium text-blue-800">Instructions</div>
                <div className="text-sm text-blue-700 mt-2 space-y-1">
                  <div>• Entrez la quantité réellement retournée pour chaque matériel</div>
                  <div>• Sélectionnez l'état dans lequel le matériel a été retourné</div>
                  <div>• Le stock sera automatiquement réintégré lors de l'enregistrement</div>
                  <div>• Cliquez sur "Finaliser le retour" pour marquer cette livraison comme terminée</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-yellow-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <div className="text-sm font-medium text-yellow-800">Attention</div>
                <div className="text-sm text-yellow-700 mt-1">
                  • Les matériels marqués comme "perdus" ne seront pas réintégrés au stock.<br/>
                  • Les matériels "endommagés" seront réintégrés mais nécessiteront une vérification.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GestionRetours;